<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-user-secret"></i> {{ _('Anonymization') }}</h3>
          <div class="well anonymization-intro">
            {{ _('Permanently anonymize customer PII in old orders, transactions, and reservations. This action cannot be undone.') }}
          </div>
        </div>
      </div>
      <div class="row" style="margin-bottom: 15px">
        <div class="col-md-4" data-search="anonymization-cutoff">
          <label>{{ _('Anonymize records up to') }}</label>
          <DatePicker :tz="Intl.DateTimeFormat().resolvedOptions().timeZone" :shortLocale="shortLocale" v-model="cutoffDate" :placeholder="_('Cutoff date')" :max-date="maxDate" style="width: 100%" />
        </div>
        <div class="col-md-4" data-search="anonymization-display-mode">
          <label>{{ _('Display mode for anonymized records') }}</label>
          <select class="form-control" v-model="displayMode" v-on:change="saveDisplayMode">
            <option value="raw">{{ _('Raw data') }}</option>
            <option value="labels">{{ _('Static labels') }}</option>
            <option value="fake">{{ _('Fake data') }}</option>
          </select>
        </div>
      </div>
      <div class="row" style="margin-bottom: 15px">
        <div class="col-md-12">
          <label>{{ _('Include in anonymization') }}</label>
          <div class="anonymization-checkboxes">
            <label class="anonymization-choice"><input type="checkbox" v-model="includeOrders" v-on:change="loadPreview" /> {{ _('Orders') }}</label>
            <label class="anonymization-choice"><input type="checkbox" v-model="includeBills" v-on:change="loadPreview" /> {{ _('Transactions') }}</label>
            <label class="anonymization-choice"><input type="checkbox" v-model="includeReservations" v-on:change="loadPreview" /> {{ _('Reservations') }}</label>
          </div>
        </div>
      </div>
      <div class="row" style="margin-bottom: 15px">
        <div class="col-md-12" data-search="customer-deletion">
          <label>{{ _('Customer deletion') }}</label>
          <div class="anonymization-radios">
            <label class="anonymization-choice"><input type="radio" value="none" v-model="customerCleanup" v-on:change="loadPreview" /> {{ _('Do not delete customers') }}</label>
            <label class="anonymization-choice"><input type="radio" value="orphaned_from_run" v-model="customerCleanup" v-on:change="loadPreview" /> {{ _('Delete customers orphaned by this run') }}</label>
            <label class="anonymization-choice"><input type="radio" value="all_unreferenced" v-model="customerCleanup" v-on:change="loadPreview" /> {{ _('Delete all unreferenced customers') }}</label>
          </div>
          <p class="help-block text-muted" v-if="customerCleanup !== 'none'">{{ _('Customer records will be permanently deleted, not anonymized.') }}</p>
        </div>
      </div>
      <div class="row" v-if="preview">
        <div class="col-md-12">
          <h4>{{ _('Preview') }}</h4>
          <table class="table table-condensed" style="max-width: 480px">
            <tbody>
              <tr v-if="includeOrders"><td>{{ _('Orders') }}</td><td>{{ preview.orders }}</td></tr>
              <tr v-if="includeOrders"><td>{{ _('Emails') }}</td><td>{{ preview.emails }}</td></tr>
              <tr v-if="includeBills"><td>{{ _('Transactions') }}</td><td>{{ preview.bills }}</td></tr>
              <tr v-if="includeReservations"><td>{{ _('Reservations') }}</td><td>{{ preview.reservations }}</td></tr>
              <tr v-if="customerCleanup !== 'none'"><td>{{ _('Customers to delete') }}</td><td>{{ preview.customers }}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <button class="btn btn-danger" type="button" :disabled="running || !canRun" v-on:click="confirmRun">
            <i class="fa fa-fw fa-exclamation-triangle"></i> {{ running ? _('Running...') : _('Run anonymization') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import components from '~/lib/components'
import DatePicker from '~/components/DatePicker.vue'

export default {
  mixins: [components],
  components: { DatePicker },
  data () {
    return {
      cutoffDate: DateTime.now().minus({ years: 3 }).toJSDate(),
      maxDate: new Date(),
      includeOrders: true,
      includeBills: true,
      includeReservations: true,
      customerCleanup: 'none',
      displayMode: 'labels',
      preview: null,
      running: false,
      requestId: null,
      previewTimer: null,
      pollGeneration: 0,
      jobFinished: false
    }
  },
  computed: {
    canRun () {
      if (!this.cutoffDate || !this.preview) {
        return false
      }
      let total = 0
      if (this.includeOrders) {
        total += this.preview.orders || 0
      }
      if (this.includeBills) {
        total += this.preview.bills || 0
      }
      if (this.includeReservations) {
        total += this.preview.reservations || 0
      }
      if (this.customerCleanup !== 'none') {
        total += this.preview.customers || 0
      }
      return total > 0
    },
    cutoffParam () {
      if (!this.cutoffDate) {
        return ''
      }
      const d = this.cutoffDate.toDate ? this.cutoffDate.toDate() : this.cutoffDate
      return DateTime.fromJSDate(d).toFormat('yyyy-MM-dd')
    }
  },
  watch: {
    cutoffDate () {
      this.schedulePreview()
    }
  },
  methods: {
    makeRequestId () {
      const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
      let rtn = ''
      for (let i = 0; i < 8; i++) {
        rtn += ALPHABET.charAt(Math.floor(Math.random() * ALPHABET.length))
      }
      this.requestId = rtn
    },
    schedulePreview () {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer)
      }
      this.previewTimer = setTimeout(() => this.loadPreview(), 400)
    },
    previewQuery () {
      const params = new URLSearchParams({
        apikey: this.apiKey,
        cutoff: this.cutoffParam,
        orders: this.includeOrders ? '1' : '0',
        bills: this.includeBills ? '1' : '0',
        reservations: this.includeReservations ? '1' : '0',
        customer_cleanup: this.customerCleanup
      })
      return params.toString()
    },
    async loadPreview () {
      if (!this.cutoffParam) {
        this.preview = null
        return
      }
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/anonymization/preview.json?' + this.previewQuery())
        this.preview = response.data
      } catch (e) {
        this.preview = null
      }
    },
    async loadDisplayMode () {
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/anonymization/displaymode.json?apikey=' + this.apiKey)
        if (response.data && response.data.anonymized_display_mode) {
          this.displayMode = response.data.anonymized_display_mode
        }
      } catch (e) {}
    },
    async saveDisplayMode () {
      try {
        await axios.put(this.baseDomain + this.shopId + '/anonymization/displaymode.json?apikey=' + this.apiKey, {
          anonymized_display_mode: this.displayMode
        })
        this.showNotification(this._('Display mode saved.'))
      } catch (e) {
        this.showError(this._('Failed to save display mode.'))
      }
    },
    confirmRun () {
      if (!this.preview || !this.canRun) {
        return
      }
      let lines = []
      if (this.includeOrders) {
        lines.push(this._('Orders') + ': ' + this.preview.orders)
        lines.push(this._('Emails') + ': ' + this.preview.emails)
      }
      if (this.includeBills) {
        lines.push(this._('Transactions') + ': ' + this.preview.bills)
      }
      if (this.includeReservations) {
        lines.push(this._('Reservations') + ': ' + this.preview.reservations)
      }
      if (this.customerCleanup !== 'none') {
        lines.push(this._('Customers to delete') + ': ' + this.preview.customers)
      }
      this.showDialog({
        title: this._('Run anonymization?'),
        text: this._('This process is irreversible. The following records will be affected:'),
        fields: [{ type: 'info', text: lines.join('\n') }],
        buttons: [
          { title: this._('Cancel'), icon: 'times', handler: () => {} },
          {
            title: this._('Run anonymization'),
            icon: 'exclamation-triangle',
            class: 'danger',
            handler: () => this.runAnonymization()
          }
        ],
        clickToClose: false
      })
    },
    formatCompletionMessage (result) {
      let msg = this._('Anonymization completed.')
      if (!result) {
        return msg
      }
      try {
        const parsed = typeof result === 'string' ? JSON.parse(result) : result
        msg += ' ' + this._('Orders') + ': ' + (parsed.orders || 0)
        msg += ', ' + this._('Transactions') + ': ' + (parsed.bills || 0)
        msg += ', ' + this._('Reservations') + ': ' + (parsed.reservations || 0)
        if (parsed.customers) {
          msg += ', ' + this._('Customers deleted') + ': ' + parsed.customers
        }
      } catch (x) {}
      return msg
    },
    handleJobComplete (result) {
      if (this.jobFinished) {
        return
      }
      this.jobFinished = true
      this.running = false
      this.pollGeneration++
      this.showNotification(this.formatCompletionMessage(result))
      this.loadPreview()
    },
    handleJobFailed () {
      if (this.jobFinished) {
        return
      }
      this.jobFinished = true
      this.running = false
      this.pollGeneration++
      this.showError(this._('Anonymization failed.'))
    },
    async runAnonymization () {
      if (this.running) {
        return
      }
      this.makeRequestId()
      const requestId = this.requestId
      const generation = ++this.pollGeneration
      this.jobFinished = false
      this.running = true
      try {
        await axios.post(this.baseDomain + this.shopId + '/anonymization.json?apikey=' + this.apiKey, {
          requestid: requestId,
          cutoff: this.cutoffParam,
          orders: this.includeOrders,
          bills: this.includeBills,
          reservations: this.includeReservations,
          customer_cleanup: this.customerCleanup
        })
        this.showNotification(this._('Anonymization job queued.'))
        this.pollJobStatus(requestId, generation)
      } catch (e) {
        this.running = false
        this.pollGeneration++
        if (e.response && e.response.data && e.response.data.errorMessage) {
          this.showError(e.response.data.errorMessage)
        } else {
          this.showError(this._('Failed to start anonymization.'))
        }
      }
    },
    async pollJobStatus (requestId, generation) {
      const maxAttempts = 120
      for (let i = 0; i < maxAttempts; i++) {
        if (generation !== this.pollGeneration || this.jobFinished) {
          return
        }
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 3000))
        }
        if (generation !== this.pollGeneration || this.jobFinished) {
          return
        }
        try {
          const response = await axios.get(this.baseDomain + this.shopId + '/jobstatus/' + requestId + '.json?apikey=' + this.apiKey)
          const rows = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : [])
          const done = rows.find(r => r.status === 'done')
          const fail = rows.find(r => r.status === 'fail')
          if (done) {
            let result = done.result
            if (!result && done.ref) {
              result = done.ref
            }
            this.handleJobComplete(result)
            return
          }
          if (fail) {
            this.handleJobFailed()
            return
          }
        } catch (e) {}
      }
      if (generation === this.pollGeneration && !this.jobFinished) {
        this.running = false
        this.showNotification(this._('Anonymization is still running. Check back later.'))
      }
    }
  },
  mounted () {
    this.loadDisplayMode()
    this.loadPreview()
    if (!this.events) {
      this.events = {
        taskUpdate: (message) => {
          if (!message || !message.data || message.data.requestid !== this.requestId) {
            return
          }
          if (message.data.status === 'done') {
            this.handleJobComplete(message.data.ref)
          } else if (message.data.status === 'fail') {
            this.handleJobFailed()
          }
        }
      }
    }
    this.enableEventBus()
  }
}

</script>

<style scoped>
.anonymization-intro {
  margin-bottom: 15px;
}

.anonymization-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.anonymization-radios {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.anonymization-choice {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-weight: normal;
  cursor: pointer;
}

.anonymization-choice input {
  margin: 0;
}
</style>
